import React, { useContext, useEffect, useRef, useState } from "react";

import PropTypes from "prop-types";

import { FormHelperText, Grid } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import MDTypography from "components/MDTypography";

import MDButton from "components/MDButton";

import MDInput from "components/MDInput";

import MDBox from "components/MDBox";

import FullScreenLoader from "components/Custom/FullScreenLoader";

import { themedDialog } from "components/Custom/AreYouSurePopUp";

import { UserContext } from "App";

import { useDispatch } from "react-redux";

import { useNavigate, useParams } from "react-router-dom";

import { EditBlogApi, GetBlogDetails, uploadImageBlog } from "APIs/BlogAPIs";

import Swal from "sweetalert2";

import { notify } from "App";

import { DeleteBlogImagesAPI } from "APIs/BlogAPIs";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "edit-blog-form";

// Required fields (checked on blur and when Save is clicked)
const FIELDS = [
  { name: "title", label: "Title" },
  { name: "description", label: "Description" },
];

const getFieldError = ({ name, label }, data) =>
  String(data?.[name] ?? "").trim() === "" ? `${label} is required` : "";

const IMAGE_REQUIRED_MSG = "At least one image is required.";

const LAST_IMAGE_MSG = "A blog needs at least one image. Add a new image before removing this one.";

// Thumbnail frame. `isNew` = image selected but not uploaded yet (gold border).
const tileSx =
  (isNew = false) =>
  ({ palette, borders }) => ({
    position: "relative",
    display: "block",
    width: "100%",
    height: 140,
    overflow: "hidden",
    textDecoration: "none",
    borderRadius: borders.borderRadius.lg,
    backgroundColor: palette.light.main,
    border: isNew
      ? `2px solid ${palette.secondary.main}`
      : `${borders.borderWidth[1]} solid ${borders.borderColor}`,
  });

// Image with a round "x" button in the corner
const ImageTile = ({ src, alt, isNew, onRemove }) => (
  <MDBox sx={tileSx(isNew)}>
    <img
      src={src}
      alt={alt}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
      }}
    />

    <MDBox
      component="button"
      type="button"
      onClick={onRemove}
      aria-label="Remove image"
      sx={({ palette }) => ({
        position: "absolute",
        top: 6,
        right: 6,
        width: 26,
        height: 26,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        borderRadius: "50%",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 700,
        color: palette.white.main,
        backgroundColor: palette.error.main,
        transition: "background-color .3s ease",
        "&:hover": {
          backgroundColor: palette.primary.main,
        },
      })}
    >
      ✕
    </MDBox>
  </MDBox>
);

ImageTile.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  isNew: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
};

ImageTile.defaultProps = {
  alt: "blog_img",
  isNew: false,
};

// Native file input
const UploadInput = ({ onChange }) => (
  <MDBox
    component="input"
    type="file"
    name="image"
    multiple
    accept=".png,.jpg,.jpeg,.gif,.webp,.tiff,.psd,.bmp,.ico,.icon"
    onChange={onChange}
    sx={({ palette, borders }) => ({
      width: "100%",
      padding: "10px",
      cursor: "pointer",
      fontSize: 14,
      color: palette.text.main,
      border: `1px dashed ${palette.secondary.main}`,
      borderRadius: borders.borderRadius.lg,
      "&::file-selector-button": {
        marginRight: "12px",
        padding: "6px 14px",
        border: "none",
        borderRadius: borders.borderRadius.md,
        cursor: "pointer",
        color: palette.white.main,
        backgroundColor: palette.primary.main,
        transition: "background-color .3s ease",
      },
      "&::file-selector-button:hover": {
        backgroundColor: palette.secondary.main,
      },
    })}
  />
);

UploadInput.propTypes = {
  onChange: PropTypes.func.isRequired,
};

const EditBlog = () => {
  const navigate = useNavigate();

  const userData = useContext(UserContext);

  const dispatch = useDispatch();

  const { accentName } = useAccent();

  const [blogData, setBlogData] = useState({
    title: "",
    description: "",
    blog_id: "",
  });

  // State management for existing images from server
  const [existingImages, setExistingImages] = useState([]);

  // State management for newly selected images
  const [newFiles, setNewFiles] = useState([]);

  const [isFetchingData, setIsFetchingData] = useState(true);

  const [imageError, setImageError] = useState("");

  // Full-screen loader state
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({});
  const formRef = useRef(null);
  const [scrollToErrorKey, setScrollToErrorKey] = useState(0);

  // After the errors have been rendered, scroll to the first one
  useEffect(() => {
    if (!scrollToErrorKey) return;
    formRef.current
      ?.querySelector(".Mui-error")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [scrollToErrorKey]);

  const handleBlur = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

  // An error only appears after the field is touched (blurred, or Save was clicked)
  const getVisibleError = (field) => (touched[field.name] ? getFieldError(field, blogData) : "");

  const isFormValid = FIELDS.every((field) => !getFieldError(field, blogData));

  const { id } = useParams();

  const handleBlogDataChange = (e) => {
    setBlogData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Fetch blog details
  const getBlogDetailsFunc = async (id, access_token, dispatch) => {
    const response = await GetBlogDetails(id, access_token, dispatch);
    setIsFetchingData(true);
    if (response !== null) {
      const formattedDescription = response.blog_description
        ? response.blog_description.replace(/\\n/g, "\n")
        : "";

      setBlogData((prev) => ({
        ...prev,
        title: response.blog_title || "",
        description: formattedDescription,
        blog_id: response.blog_id ? response.blog_id.toString() : "",
      }));

      if (response.image_list && Array.isArray(response.image_list)) {
        setExistingImages(response.image_list);
      }
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData?.access_token !== undefined) {
      getBlogDetailsFunc(id, userData.access_token, dispatch);
    }
  }, [userData]);

  // Handle selecting new files and creating local preview URLs
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);

      const newImageObjects = selectedFiles.map((file) => ({
        id: Math.random().toString(36).substring(2, 9),
        file: file,
        preview: URL.createObjectURL(file),
      }));

      setNewFiles((prev) => [...prev, ...newImageObjects]);

      setImageError("");

      e.target.value = "";
    }
  };

  // Delete existing image
  const handleDeleteExistingImage = (blog_image_id) => {
    // The last image can't be removed: a blog needs at least one image
    if (existingImages.length + newFiles.length === 1) {
      setImageError(LAST_IMAGE_MSG);
      return;
    }

    Swal.fire({
      title: "Remove Image?",
      text: "Are you sure you want to remove this existing image?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove it!",
      ...themedDialog,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await DeleteBlogImagesAPI(blog_image_id, userData.access_token, dispatch);

        setExistingImages((prev) => prev.filter((img) => img.blog_image_id !== blog_image_id));
      }
    });
  };

  // Delete newly added image before form submission
  const handleDeleteNewImage = (fileId) => {
    setNewFiles((prev) => {
      const fileToRemove = prev.find((item) => item.id === fileId);

      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      return prev.filter((item) => item.id !== fileId);
    });
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const hasNoImage = existingImages.length + newFiles.length === 0;

    // Invalid form or no image: show every error (missing fields + image) and scroll to the first
    if (!isFormValid || hasNoImage) {
      if (hasNoImage) setImageError(IMAGE_REQUIRED_MSG);
      if (!isFormValid) {
        setTouched(Object.fromEntries(FIELDS.map((field) => [field.name, true])));
      }
      setScrollToErrorKey((key) => key + 1);
      return;
    }

    Swal.fire({
      title: "Save changes?",
      text: "Your changes to this blog will be saved.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save",
      cancelButtonText: "Cancel",
      ...themedDialog,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);

        try {
          const editBlog = await EditBlogApi(blogData, userData.access_token, dispatch);

          if (editBlog !== null) {
            if (newFiles.length > 0) {
              let formData = new FormData();

              formData.append("blog_id", blogData?.blog_id);

              newFiles.forEach((item) => {
                formData.append("files", item.file);
              });

              await uploadImageBlog(formData, userData.access_token, dispatch);
            }

            notify("Blog Updated!!", "success");

            navigate("/blog");
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />
      <SectionHeader
        icon="edit"
        label="EDIT BLOG"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/blog")}
          >
            Back
          </MDButton>
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          <form ref={formRef} id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
            <SectionCard title="Details" subtitle="Fields marked * are required">
              <Grid container spacing={2}>
                {/* Title Input */}
                <FormField name="title" required full>
                  <MDInput
                    name="title"
                    type="text"
                    placeholder="Enter title"
                    value={blogData?.title || ""}
                    fullWidth
                    onChange={handleBlogDataChange}
                    onBlur={() => handleBlur(FIELDS[0].name)}
                    error={!!getVisibleError(FIELDS[0])}
                  />
                  {getVisibleError(FIELDS[0]) && (
                    <FormHelperText error>{getVisibleError(FIELDS[0])}</FormHelperText>
                  )}
                </FormField>

                {/* Description Input */}
                <FormField name="description" required full>
                  <MDInput
                    name="description"
                    multiline
                    rows={6}
                    placeholder="Enter description"
                    fullWidth
                    value={blogData?.description || ""}
                    onChange={handleBlogDataChange}
                    onBlur={() => handleBlur(FIELDS[1].name)}
                    error={!!getVisibleError(FIELDS[1])}
                  />
                  {getVisibleError(FIELDS[1]) && (
                    <FormHelperText error>{getVisibleError(FIELDS[1])}</FormHelperText>
                  )}
                </FormField>
              </Grid>
            </SectionCard>

            <SectionCard title="Media" subtitle="Remove existing images or add new ones">
              <Grid container spacing={2}>
                {/* Existing Images Section */}
                <Grid item xs={12}>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color={accentName}
                    display="block"
                    mb={1.5}
                  >
                    Existing Images
                  </MDTypography>

                  {existingImages.length > 0 ? (
                    <Grid container spacing={2}>
                      {existingImages.map((img) => (
                        <Grid item key={img.blog_image_id} xs={6} sm={4} md={3}>
                          <ImageTile
                            src={img.media}
                            alt="blog_img"
                            onRemove={() => handleDeleteExistingImage(img.blog_image_id)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <MDTypography variant="button" color="text">
                      No existing images found.
                    </MDTypography>
                  )}
                </Grid>

                {/* New Images Preview Section */}
                {newFiles.length > 0 && (
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color={accentName}
                      display="block"
                      mb={1.5}
                    >
                      New Images to Upload ({newFiles.length})
                    </MDTypography>

                    <Grid container spacing={2}>
                      {newFiles.map((item) => (
                        <Grid item key={item.id} xs={6} sm={4} md={3}>
                          <ImageTile
                            isNew
                            src={item.preview}
                            alt="new_preview"
                            onRemove={() => handleDeleteNewImage(item.id)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                {/* Upload Input */}
                <FormField name="upload_new_media" required full>
                  <UploadInput onChange={handleFileChange} />
                  {imageError && <FormHelperText error>{imageError}</FormHelperText>}
                </FormField>
              </Grid>
            </SectionCard>
          </form>

          {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save still submits the form through the `form` attribute. */}
          <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
            <MDButton
              type="button"
              variant="outlined"
              color="primary"
              sx={ranuOutlinedButtonSx}
              disabled={loading}
              onClick={() => navigate("/blog")}
            >
              Cancel
            </MDButton>
            <MDButton
              color="primary"
              type="submit"
              form={FORM_ID}
              sx={ranuButtonSx}
              disabled={loading}
            >
              Save Blog
            </MDButton>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default EditBlog;
